import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/userService';
import ProductCard from '../components/product/ProductCard';
import ProfileForm from '../components/user/ProfileForm';
import OrderHistory from '../components/user/OrderHistory';
import AddressManager from '../components/user/AddressManager';
import Loading from '../components/common/Loading';
import './Profile.css';

const Profile = () => {
  const { user: authUser, updateUser, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const userIdRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [profileData, ordersData, favoritesData] = await Promise.all([
        userService.getProfile(),
        userService.getOrderHistory(),
        userService.getFavorites(),
      ]);

      // Backend returns { user: ... } so extract user from response
      const userData = profileData?.user || profileData;
      
      if (userData) {
        setUser(userData);
        updateUser(userData);
      }
      setOrders(
        Array.isArray(ordersData) ? ordersData : ordersData?.orders || []
      );
      setFavorites(
        Array.isArray(favoritesData)
          ? favoritesData
          : favoritesData?.favorites || []
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [updateUser]);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    // Only load data when user ID changes (not when user object reference changes)
    // This prevents infinite loops when updateUser changes the user object
    const currentUserId = authUser?.id;
    if (currentUserId && userIdRef.current !== currentUserId) {
      userIdRef.current = currentUserId;
      loadData();
    } else if (!currentUserId) {
      // Reset ref when user logs out and stop loading
      userIdRef.current = null;
      setUser(null);
      setLoading(false);
    }
  }, [authUser?.id, loadData, authLoading]);

  // Handle orderSuccess query parameter
  useEffect(() => {
    const orderSuccess = searchParams.get('orderSuccess');
    if (orderSuccess === 'true') {
      setSuccessMessage('Order placed successfully!');
      // Reload orders to show the new order
      if (authUser) {
        userService
          .getOrderHistory()
          .then(ordersData => {
            setOrders(
              Array.isArray(ordersData) ? ordersData : ordersData?.orders || []
            );
          })
          .catch(() => {
            // Silently handle error - user can manually refresh if needed
          });
      }
      // Remove query parameter from URL to prevent re-triggering
      setSearchParams({}, { replace: true });
      // Clear success message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams, authUser]);

  const handleProfileUpdate = async data => {
    try {
      setSaving(true);
      setError(null);
      const updatedData = await userService.updateProfile({
        full_name: data.fullName,
        phone_number: data.phoneNumber,
        address: data.address,
        city: data.city,
      });
      // Backend returns { user: ... } so extract user from response
      const updatedUser = updatedData?.user || updatedData;
      if (updatedUser) {
        setUser(updatedUser);
        updateUser(updatedUser);
      }
      setIsEditing(false);
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFavorite = async (event, productId) => {
    // Prevent navigating to product detail when clicking remove
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    try {
      await userService.removeFavorite(productId);
      setFavorites(prev => prev.filter(p => p.id !== productId));
    } catch (err) {
      setError(
        err?.message || 'Failed to remove product from favorites'
      );
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1>My Profile</h1>
      </div>

      {successMessage && (
        <div
          className="success-message"
          style={{
            padding: '1rem',
            backgroundColor: '#10b981',
            color: 'white',
            borderRadius: '4px',
            marginBottom: '1rem',
          }}
        >
          {successMessage}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="profile-content">
        <div className="profile-section">
          <div className="user-details-display">
            <div className="user-details-header">
              <h3>Account Information</h3>
              <button
                className="btn-edit-profile"
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </button>
            </div>
            {!isEditing ? (
              <div className="user-details-grid">
                <div className="user-detail-item">
                  <span className="user-detail-label">Email:</span>
                  <span className="user-detail-value">
                    {user?.email || authUser?.email || 'N/A'}
                  </span>
                </div>
                <div className="user-detail-item">
                  <span className="user-detail-label">Full Name:</span>
                  <span className="user-detail-value">
                    {user?.full_name || authUser?.full_name || 'Not set'}
                  </span>
                </div>
                <div className="user-detail-item">
                  <span className="user-detail-label">Phone Number:</span>
                  <span className="user-detail-value">
                    {user?.phone_number || authUser?.phone_number || 'Not set'}
                  </span>
                </div>
                <div className="user-detail-item">
                  <span className="user-detail-label">Address:</span>
                  <span className="user-detail-value">
                    {user?.address || authUser?.address || 'Not set'}
                  </span>
                </div>
                <div className="user-detail-item">
                  <span className="user-detail-label">City:</span>
                  <span className="user-detail-value">
                    {user?.city || authUser?.city || 'Not set'}
                  </span>
                </div>
              </div>
            ) : (
              <ProfileForm
                user={user || authUser}
                onSubmit={handleProfileUpdate}
                loading={saving}
              />
            )}
          </div>
        </div>

        <div className="profile-section">
          <AddressManager />
        </div>

        <div className="profile-section">
          <OrderHistory orders={orders} />
        </div>

        <div className="profile-section">
          <h3>My Favorites</h3>
          {favorites.length === 0 ? (
            <p className="no-favorites-text">
              You haven&apos;t added any favorites yet.
            </p>
          ) : (
            <div className="favorites-grid">
              {favorites.map(product => (
                <div key={product.id} className="favorite-item">
                  <ProductCard product={product} />
                  <button
                    type="button"
                    className="btn-remove-favorite"
                    onClick={event => handleRemoveFavorite(event, product.id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
